import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { useToaster } from '../../../core/components/Toast';
import { ConnectionStatus, getPersonalSmartInboxConnectionStatus } from '../../../document/constants';
import { PersonalSmartInboxIntegration } from './PersonalSmartInboxIntegration';
import { CONNECT_PERSONAL_MAILBOX_AUTH, PERSONAL_SMART_INBOX_INTEGRATIONS } from '../../queries';
import { usePersonalInboxConnection } from '../../hooks/usePersonalInboxConnection';

interface PersonalSmartInboxSectionProps {
    integration: any;
    isAdminConsentGranted: boolean;
    isAdminConsentUpToDate: boolean;
    onRefresh?: () => Promise<any>;
    onClose?: () => void;
}

const PersonalSmartInboxSection = ({
    integration,
    isAdminConsentGranted,
    isAdminConsentUpToDate,
    onRefresh,
    onClose,
}: PersonalSmartInboxSectionProps) => {
    const { t } = useTranslation('users');
    const { publishToast } = useToaster();

    const authForm = useForm({
        defaultValues: { status: getPersonalSmartInboxConnectionStatus(integration) },
        mode: 'onTouched',
    });
    const [connectionFailedAt, setConnectionFailedAt] = useState<string | null>(integration.connectionFailedAt ?? null);
    const channelForm = useForm({ defaultValues: { orderChannel: '', rfqChannel: '' }, mode: 'onTouched' });
    const excludedContentForm = useForm({
        defaultValues: {
            excludedContent: {
                keywords: integration.manualProcessingKeywords || [],
                emailsDomains: integration.manualProcessingSenderEmails || [],
            },
        },
        mode: 'onTouched',
    });

    const [connectOutlook] = useMutation(CONNECT_PERSONAL_MAILBOX_AUTH);
    const { data, refetch: refetchIntegrations } = useQuery(PERSONAL_SMART_INBOX_INTEGRATIONS, {
        fetchPolicy: 'no-cache',
    });

    const personalInboxConnection = usePersonalInboxConnection({ t, authForm });
    const [connectedAgentName, setConnectedAgentName] = useState<string | null>(integration.agent?.name || null);

    const applyIntegrationData = (updated: any) => {
        authForm.reset({ status: getPersonalSmartInboxConnectionStatus(updated) });
        setConnectionFailedAt(updated.connectionFailedAt ?? null);
        personalInboxConnection.setIsConnected(true);
        personalInboxConnection.setConnectedChannels(
            [
                updated.rfqChannel && { rfqChannel: updated.rfqChannel.name },
                updated.orderChannel && { orderChannel: updated.orderChannel.name },
            ].filter(Boolean)
        );
        excludedContentForm.reset({
            excludedContent: {
                keywords: updated.manualProcessingKeywords || [],
                emailsDomains: updated.manualProcessingSenderEmails || [],
            },
        });
        setConnectedAgentName(updated.agent?.name || null);
    };

    useEffect(() => {
        const fresh = data?.personalSmartInboxIntegrations?.find((i: any) => i.id === integration.id);
        if (fresh) {
            applyIntegrationData(fresh);
        }
    }, [data]);

    const refetchPersonalMailboxStatus = async () => {
        try {
            const { data: fresh } = await refetchIntegrations();
            const updated = fresh?.personalSmartInboxIntegrations?.find((i: any) => i.id === integration.id);
            if (updated) {
                applyIntegrationData(updated);
                await onRefresh?.();
            } else {
                await onRefresh?.();
                onClose?.();
                authForm.reset({ status: ConnectionStatus.Disconnected });
                personalInboxConnection.setIsConnected(false);
                personalInboxConnection.setConnectedChannels([]);
                return;
            }
        } catch (error) {
            publishToast({ description: t('personalSmartInbox.errorFetchStatus'), status: 'error' });
        }
    };

    return (
        <PersonalSmartInboxIntegration
            translate={t}
            isAdminConsentGranted={isAdminConsentGranted}
            isAdminConsentUpToDate={isAdminConsentUpToDate}
            authForm={authForm}
            excludedContentForm={excludedContentForm}
            channelForm={channelForm}
            connectOutlook={connectOutlook}
            refetchPersonalMailboxStatus={refetchPersonalMailboxStatus}
            personalSmartInboxId={integration.id}
            connectionFailedAt={connectionFailedAt}
            connectedAgentName={connectedAgentName}
            sectionClassnames="border-none! m-auto p-0 pb-6"
            onSaveSuccess={onClose}
            {...personalInboxConnection}
        />
    );
};

export default PersonalSmartInboxSection;
